import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import JobsPanel from "@/components/research/JobsPanel";

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { format } from "date-fns";

type Cap = {
  id: string;
  scope: string;
  scope_id: string | null;
  monthly_cap_usd: number;
  hard_block: boolean;
  warn_at_pct: number;
  current_spend_usd: number;
  current_period_start: string;
};

const SCOPES = ["global", "user", "action", "model"];

export default function ResearchCosts() {
  const { data: canManage, isLoading: capLoading } = useResearchCapability("manage");
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<Cap> | null>(null);

  const { data: caps, isLoading } = useQuery({
    queryKey: ["research_cost_caps"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_ai_cost_caps")
        .select("*")
        .order("scope")
        .order("scope_id");
      if (error) throw error;
      return data as Cap[];
    },
    enabled: !!canManage,
  });

  const { data: runs } = useQuery({
    queryKey: ["research_call_runs_mtd"],
    queryFn: async () => {
      const start = new Date();
      start.setUTCDate(1);
      start.setUTCHours(0, 0, 0, 0);
      const { data, error } = await supabase
        .from("research_ai_call_runs")
        .select("action,model,cost_usd,cache_hit,status,created_at")
        .gte("created_at", start.toISOString())
        .limit(5000);
      if (error) throw error;
      return data as Array<{ action: string; model: string; cost_usd: number; cache_hit: boolean; status: string }>;
    },
    enabled: !!canManage,
  });

  const byAction = useMemo(() => agg(runs ?? [], "action"), [runs]);
  const byModel = useMemo(() => agg(runs ?? [], "model"), [runs]);
  const totalSpend = useMemo(() => (runs ?? []).reduce((s, r) => s + Number(r.cost_usd ?? 0), 0), [runs]);
  const totalCalls = runs?.length ?? 0;
  const cacheHits = (runs ?? []).filter((r) => r.cache_hit).length;
  const failures = (runs ?? []).filter((r) => r.status !== "ok" && r.status !== "success").length;

  const save = useMutation({
    mutationFn: async (c: Partial<Cap>) => {
      const payload: any = { ...c };
      if (payload.scope === "global") payload.scope_id = null;
      const { error } = payload.id
        ? await supabase.from("research_ai_cost_caps").update(payload).eq("id", payload.id)
        : await supabase.from("research_ai_cost_caps").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["research_cost_caps"] });
      setEditing(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (capLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!canManage) return <p className="text-sm text-muted-foreground">Requires manage capability.</p>;

  return (
    <div className="space-y-6">
      <AiKillSwitch />
      <JobsPanel />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="MTD spend" value={`$${totalSpend.toFixed(2)}`} />
        <Stat label="Calls" value={totalCalls.toLocaleString()} />
        <Stat label="Cache hits" value={totalCalls ? `${((cacheHits / totalCalls) * 100).toFixed(0)}%` : "—"} />
        <Stat label="Failures" value={failures.toString()} />
      </div>

      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Cost caps</h2>
        <Button onClick={() => setEditing({ scope: "global", monthly_cap_usd: 100, hard_block: false, warn_at_pct: 80 })}>
          <Plus className="h-4 w-4 mr-1" /> New cap
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-sm text-muted-foreground p-4">Loading caps…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scope</TableHead>
                  <TableHead>Scope id</TableHead>
                  <TableHead>Cap (USD/mo)</TableHead>
                  <TableHead>Used</TableHead>
                  <TableHead>Utilisation</TableHead>
                  <TableHead>Warn @</TableHead>
                  <TableHead>Hard block</TableHead>
                  <TableHead>Period start</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {caps?.map((c) => {
                  const used = Number(c.current_spend_usd ?? 0);
                  const cap = Number(c.monthly_cap_usd ?? 0);
                  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
                  const breach = pct >= 100;
                  const warn = !breach && pct >= (c.warn_at_pct ?? 80);
                  return (
                    <TableRow key={c.id}>
                      <TableCell><Badge variant="outline">{c.scope}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{c.scope_id ?? "—"}</TableCell>
                      <TableCell>${cap.toFixed(2)}</TableCell>
                      <TableCell>${used.toFixed(2)}</TableCell>
                      <TableCell className="w-48">
                        <div className="flex items-center gap-2">
                          <Progress value={pct} className="h-2" />
                          <span className={`text-xs ${breach ? "text-destructive" : warn ? "text-amber-600" : "text-muted-foreground"}`}>
                            {pct.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{c.warn_at_pct}%</TableCell>
                      <TableCell>{c.hard_block ? <Badge variant="destructive">on</Badge> : <Badge variant="secondary">off</Badge>}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {c.current_period_start ? format(new Date(c.current_period_start), "dd/MM/yyyy") : "—"}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="MTD spend by action" data={byAction} />
        <ChartCard title="MTD spend by model" data={byModel} />
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit cap" : "New cap"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Field label="Scope">
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={editing.scope ?? "global"}
                  onChange={(e) => setEditing({ ...editing, scope: e.target.value, scope_id: e.target.value === "global" ? null : editing.scope_id })}
                >
                  {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              {editing.scope !== "global" && (
                <Field label={`Scope id (${editing.scope})`}>
                  <Input
                    value={editing.scope_id ?? ""}
                    onChange={(e) => setEditing({ ...editing, scope_id: e.target.value })}
                    placeholder={editing.scope === "user" ? "user uuid" : editing.scope === "action" ? "snake_case action" : "model slug"}
                  />
                </Field>
              )}
              <Field label="Monthly cap (USD)">
                <Input type="number" step="0.01" value={editing.monthly_cap_usd ?? 0} onChange={(e) => setEditing({ ...editing, monthly_cap_usd: Number(e.target.value) })} />
              </Field>
              <Field label="Warn at (%)">
                <Input type="number" min={1} max={100} value={editing.warn_at_pct ?? 80} onChange={(e) => setEditing({ ...editing, warn_at_pct: Number(e.target.value) })} />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={!!editing.hard_block} onCheckedChange={(v) => setEditing({ ...editing, hard_block: v })} />
                Hard block when cap reached
              </label>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => save.mutate(editing)} disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card><CardContent className="p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </CardContent></Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

function agg(runs: Array<{ cost_usd: number; [k: string]: any }>, key: string) {
  const m = new Map<string, { name: string; spend: number; calls: number }>();
  for (const r of runs) {
    const k = String(r[key] ?? "—");
    const cur = m.get(k) ?? { name: k, spend: 0, calls: 0 };
    cur.spend += Number(r.cost_usd ?? 0);
    cur.calls += 1;
    m.set(k, cur);
  }
  return [...m.values()].sort((a, b) => b.spend - a.spend).slice(0, 15);
}

function ChartCard({ title, data }: { title: string; data: Array<{ name: string; spend: number; calls: number }> }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-3 text-sm">{title}</h3>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No calls this month.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(240, data.length * 28)}>
            <BarChart data={data} layout="vertical" margin={{ left: 20, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => `$${v.toFixed(2)}`} />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: any, n: string) => n === "spend" ? `$${Number(v).toFixed(4)}` : v} />
              <Bar dataKey="spend" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function AiKillSwitch() {
  const qc = useQueryClient();
  const { data: flag, isLoading } = useQuery({
    queryKey: ["feature_flag", "ai_kill_switch"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("id, enabled")
        .eq("flag_key", "ai_kill_switch")
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; enabled: boolean } | null;
    },
    refetchInterval: 15000,
  });

  const toggle = useMutation({
    mutationFn: async (next: boolean) => {
      if (!flag?.id) throw new Error("ai_kill_switch flag missing");
      const { error } = await supabase
        .from("feature_flags")
        .update({ enabled: next, updated_at: new Date().toISOString() })
        .eq("id", flag.id);
      if (error) throw error;
    },
    onSuccess: (_d, next) => {
      toast.success(next ? "AI brake ENGAGED — all calls blocked" : "AI brake released");
      qc.invalidateQueries({ queryKey: ["feature_flag", "ai_kill_switch"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const engaged = !!flag?.enabled;

  return (
    <Card className={engaged ? "border-destructive bg-destructive/5" : ""}>
      <CardContent className="p-4 flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm">Hard brake — AI kill switch</h3>
            {engaged && <Badge variant="destructive">ENGAGED</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Blocks every call routed through <code>research-ai-assist</code> (assist, expand-query, extract-source, generate-questions, triage, etc.). Returns 503 immediately. Toggle off to resume.
          </p>
        </div>
        <Switch
          checked={engaged}
          disabled={isLoading || toggle.isPending || !flag}
          onCheckedChange={(v) => toggle.mutate(v)}
        />
      </CardContent>
    </Card>
  );
}
